import { Breadcrumbs } from "components/Breadcrumbs";
import { ConfiguredProductImage } from "components/ConfiguredProductImage";
import { AccountOverviewList } from "components/Favorites/interfaces";
import Layout from "components/Layout";
import { buildOrderedUrlParamMap } from "components/ProductCodeConfigurator/helpers";
import { ProductDetailTabs } from "components/ProductDetailTabs";
import Spacer from "components/Spacer";
import {
    VisualConfigurator,
    buildVisualizerParams,
} from "components/VisualConfigurator";
import { ProductProvider, UserStatus } from "components/productContext";
import {
    FetchError,
    readCMSDataClientSide,
    readCMSDataServerSide,
} from "helpers/api";
import { useHideDebugContent } from "helpers/debug";
import urls from "helpers/urls";
import { PaddingValue, filterOutNulls } from "helpers/utils";
import { DRFPaginatedQuery, StreamfieldBlock } from "interfaces";
import type {
    AttrOptionApiResult,
    AttributeOptionDetails,
    ProductLink,
} from "interfaces/products";
import type { GetServerSidePropsContext } from "next";
import type {
    EmtekPageProps,
    InferGetWagtailServerSidePropsType,
} from "pages/[[...slug]]";
import { EmtekClientProvider } from "queryClient";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { CloudinaryImage } from "react-tiny-cloudinary";
import layoutStyles from "styles/layout.module.scss";

import Caret from "../../public/svg/caret.svg";
import MyEmtek from "../../public/svg/my-emtek.svg";
import { Toolbox, ToolboxBlock } from "./Toolbox";

import styles from "./product-page.module.scss";

export type ProductPageProps = EmtekPageProps<{
    attribute: AttributeOptionDetails;
    image_padding: PaddingValue;
    body: (ToolboxBlock | StreamfieldBlock<"product_detail_tabs", unknown>)[];
}>;

export async function fetchFavoritesLists() {
    const response: DRFPaginatedQuery<AccountOverviewList> =
        await readCMSDataClientSide("/api/favorites", {});
    return response;
}

export async function fetchAccountInfo(
    setUserStatus: Dispatch<SetStateAction<UserStatus>>,
) {
    try {
        await readCMSDataClientSide("/api/auth/account", {});
        setUserStatus("registered");
    } catch (error) {
        error instanceof FetchError &&
            setUserStatus(
                error.response.status === 401 ? "lazy" : "unregistered",
            );
    }
}

export default function ProductPage({
    wagtail,
    menus,
    displayImage,
    configString,
    productCodes,
    selectedOptionIds,
}: InferGetWagtailServerSidePropsType<typeof fetchData>) {
    const [favoritesLists, setFavoritesLists] = useState<AccountOverviewList[]>(
        [],
    );
    const [userStatus, setUserStatus] = useState<UserStatus>("unregistered");
    const [loading, setLoading] = useState(false);
    const breadcrumbs = wagtail.ancestors.slice(1, -1);

    let mainImageUrl: string | undefined;
    let isPerfectMatch = false;
    let caption: string | undefined;
    if (displayImage) {
        mainImageUrl = displayImage.url;
        isPerfectMatch = displayImage.is_exact_match;
        caption = displayImage.caption;
    } else if (wagtail.attribute.primary_image) {
        mainImageUrl = wagtail.attribute.primary_image.image;
        /**
         * If no options have been selected we use the primary image
         * of the product family, which is treated like a perfect match.
         * Otherwise it means we were unable to find a match
         */
        isPerfectMatch = selectedOptionIds.length === 0;
        caption = wagtail.attribute.primary_image.title;
    }

    const hideForDebug = useHideDebugContent();

    const selectedOptions = selectedOptionIds
        .map((id) =>
            wagtail.attribute.configurator
                .flatMap((conf) => conf.options)
                .find((opt) => `${opt.id}` === id),
        )
        .filter(filterOutNulls);

    useEffect(() => {
        fetchFavoritesLists().then((response) =>
            setFavoritesLists(response.results),
        );
        fetchAccountInfo(setUserStatus);
    }, []);

    return (
        <EmtekClientProvider>
            <ProductProvider
                details={wagtail.attribute}
                favoriteItem={null}
                favoriteItemPieces={{
                    image: mainImageUrl ? [String(mainImageUrl)] : [],
                    page: wagtail.id,
                    choices: selectedOptions.map((option) => ({
                        option: option.id,
                        attribute: option.attribute.id,
                    })),
                    title: wagtail.attribute.title,
                }}
                favoritesLists={favoritesLists}
                setFavoritesList={setFavoritesLists}
                userStatus={userStatus}
                setUserStatus={setUserStatus}
                productCodes={productCodes}
                pageTitle={{
                    configString,
                    pageTitle: wagtail.title,
                }}
            >
                <Layout menus={menus} metadata={wagtail}>
                    <main>
                        <nav
                            aria-label="Breadcrumbs"
                            className={styles.breadcrumbs}
                        >
                            <Breadcrumbs breadcrumbs={breadcrumbs} />
                        </nav>
                        {wagtail.has_ruleset && (
                            <a
                                className={styles.visualizerTrigger}
                                href={urls.visualizer.concat(
                                    buildVisualizerParams(
                                        wagtail.id,
                                        selectedOptions,
                                        wagtail.attribute.configurator,
                                    ),
                                )}
                            >
                                <div>
                                    <CloudinaryImage
                                        type="upload"
                                        cldId={"my-emtek--knobs_hogozb"}
                                        cldSrcSet={[500, 768, 1200]}
                                    >
                                        <img alt="" sizes="800px" />
                                    </CloudinaryImage>
                                </div>
                                <div className={styles.visualizerTriggerCopy}>
                                    <div>
                                        <MyEmtek />
                                    </div>
                                    <div>
                                        <p>
                                            Design your products exactly how you
                                            want them with our NEW MyEMTEK tool,
                                            and save the details to your
                                            Favorites.
                                        </p>
                                        <span>
                                            Create Now <Caret />
                                        </span>
                                    </div>
                                </div>
                            </a>
                        )}
                        <section className={styles.visualConfigurator}>
                            <ConfiguredProductImage
                                images={[mainImageUrl]}
                                noApplicableImage={!isPerfectMatch}
                                caption={caption}
                                pageLoading={loading}
                                pad={wagtail.image_padding}
                            />
                            <div>
                                <VisualConfigurator
                                    pageTitle={wagtail.title}
                                    pageID={wagtail.id}
                                    configurators={
                                        wagtail.attribute.configurator
                                    }
                                    displayToggleIDs={
                                        wagtail.attribute.finish_ids
                                    }
                                    selectedOptions={selectedOptions}
                                    setLoading={setLoading}
                                    customizable={wagtail.has_ruleset}
                                />
                            </div>
                        </section>
                        <Spacer height="large" />
                        {wagtail.body.every(
                            (block) => block.type !== "product_detail_tabs",
                        ) && (
                            <div className={layoutStyles.container}>
                                <ProductDetailTabs
                                    attribute={wagtail.attribute}
                                    selectedOptions={selectedOptions}
                                    loading={loading}
                                    setLoading={setLoading}
                                />
                            </div>
                        )}
                        {wagtail.body.map((block, index) => {
                            switch (block.type) {
                                case "product_detail_tabs":
                                    return (
                                        <div
                                            key={block.id}
                                            className={layoutStyles.container}
                                        >
                                            <ProductDetailTabs
                                                key={block.id}
                                                attribute={wagtail.attribute}
                                                selectedOptions={
                                                    selectedOptions
                                                }
                                                loading={loading}
                                                setLoading={setLoading}
                                            />
                                        </div>
                                    );
                                default:
                                    return (
                                        <Toolbox
                                            key={block.id}
                                            block={block}
                                            isFirstBlock={index === 0}
                                        />
                                    );
                            }
                        })}
                        <pre
                            hidden={hideForDebug}
                            style={{ maxWidth: "99vw", overflow: "scroll" }}
                        >
                            <code>
                                {JSON.stringify(wagtail.attribute, null, 2)}
                            </code>
                        </pre>
                        <Spacer height="xxlarge" />
                    </main>
                </Layout>
            </ProductProvider>
        </EmtekClientProvider>
    );
}

type ConfigState =
    | {
          status: "bad" | "incomplete";
          product: null;
      }
    | {
          status: "complete";
          product: ProductLink;
      };

function getConfigState({ results }: AttrOptionApiResult): ConfigState {
    if (results.length === 1) {
        return {
            status: "complete",
            product: results[0],
        };
    }
    return {
        status: results.length === 0 ? "bad" : "incomplete",
        product: null,
    };
}

export async function fetchData(
    context: GetServerSidePropsContext,
    props: ProductPageProps,
    apiResponse: Response,
) {
    const query = new URL(context.resolvedUrl, `https://example.com`)
        .searchParams;

    const urlParamMap = buildOrderedUrlParamMap(query, props.wagtail.attribute);

    const matchingProducts = (await readCMSDataServerSide(
        `/api/oscar/products/`,
        {
            context: context,
            search: urlParamMap,
        },
    )) as AttrOptionApiResult;

    return {
        props: {
            ...props,
            displayImage: matchingProducts.display_image,
            configString: matchingProducts.config_string,
            productCodes: matchingProducts.results.map(
                (result) => result.title,
            ),
            configState: getConfigState(matchingProducts),
            selectedOptionIds: [...query.values()].filter((value) =>
                props.wagtail.attribute.configurator
                    .flatMap((conf) => conf.options)
                    .some((opt) => `${opt.id}` === value),
            ),
        },
        apiResponse: apiResponse,
    };
}
